import React, { useState, useEffect } from 'react';
import { 
  Printer, Settings, Plus, Trash2, Edit2, Check, X, 
  RefreshCw, Truck, Store, ChefHat, FileText, Save,
  ToggleLeft, ToggleRight, Usb, Wifi, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../lib/api';
import { MainLayout } from '@/components/Layout';
import { thermalPrinter } from '@/services/thermalPrinter';

const PrinterSettingsPage = () => {
  const [storeId, setStoreId] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [deliveryConfig, setDeliveryConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddPrinter, setShowAddPrinter] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  
  // Estado para impressora USB
  const [usbPrinter, setUsbPrinter] = useState({
    isSupported: thermalPrinter.isSupported(),
    isConnected: false,
    deviceName: null,
    connecting: false
  });
  
  // Estado para copias da impressora termica
  const [thermalCopies, setThermalCopies] = useState(thermalPrinter.getCopies());
  const [showAccessDeniedHelp, setShowAccessDeniedHelp] = useState(false);
  
  const [newPrinter, setNewPrinter] = useState({
    name: '',
    printer_type: 'RESTAURANT',
    ip_address: '',
    port: 9100,
    paper_width: 80,
    is_active: true,
    print_copies: 1,
    auto_cut: true
  });

  // Verificar estado da impressora USB ao montar
  useEffect(() => {
    setUsbPrinter(prev => ({
      ...prev,
      isConnected: thermalPrinter.isConnected
    }));
  }, []);

  // Get store ID from user
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setStoreId(user.store_id);
    }
  }, []);

  // Load data
  useEffect(() => {
    if (storeId) {
      loadPrinters();
      loadDeliveryConfig();
    }
  }, [storeId]);

  const loadPrinters = async () => {
    try {
      const res = await apiClient.get(`/printers/${storeId}`);
      setPrinters(res.data);
    } catch (err) {
      console.error('Error loading printers:', err);
    }
  };

  const loadDeliveryConfig = async () => {
    try {
      const res = await apiClient.get(`/delivery-config/${storeId}`);
      setDeliveryConfig(res.data);
    } catch (err) {
      console.error('Error loading delivery config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrinter = async () => {
    if (!newPrinter.name) {
      toast.error('Nome da impressora é obrigatório');
      return;
    }
    try {
      await apiClient.post(`/printers/${storeId}`, newPrinter);
      toast.success('Impressora cadastrada!');
      setShowAddPrinter(false);
      setNewPrinter({
        name: '',
        printer_type: 'RESTAURANT',
        ip_address: '',
        port: 9100,
        paper_width: 80,
        is_active: true,
        print_copies: 1,
        auto_cut: true
      });
      loadPrinters();
    } catch (err) {
      toast.error('Erro ao cadastrar impressora');
    }
  };

  const handleUpdatePrinter = async (printerId, data) => {
    try {
      await apiClient.put(`/printers/${storeId}/${printerId}`, data);
      toast.success('Impressora atualizada!');
      setEditingPrinter(null);
      loadPrinters();
    } catch (err) {
      toast.error('Erro ao atualizar impressora');
    }
  };

  const handleDeletePrinter = async (printerId) => {
    if (!window.confirm('Remover esta impressora?')) return;
    try {
      await apiClient.delete(`/printers/${storeId}/${printerId}`);
      toast.success('Impressora removida!');
      loadPrinters();
    } catch (err) {
      toast.error('Erro ao remover impressora');
    }
  };

  // Funções para impressora USB
  const handleConnectUsb = async () => {
    setUsbPrinter(prev => ({ ...prev, connecting: true }));
    try {
      const result = await thermalPrinter.connect();
      setUsbPrinter({
        isSupported: true,
        isConnected: true,
        deviceName: result.name,
        connecting: false
      });
      toast.success(`Impressora conectada: ${result.name}`);
      
      // Salvar no localStorage
      localStorage.setItem('usbPrinterConnected', 'true');
      localStorage.setItem('usbPrinterName', result.name);
    } catch (err) {
      setUsbPrinter(prev => ({ ...prev, connecting: false }));
      const msg = err.message || 'Erro ao conectar impressora USB';
      if (msg.includes('Access denied') || msg.includes('Acesso negado')) {
        toast.error('Acesso negado ao USB. Veja as instruções abaixo.', { duration: 5000 });
        setShowAccessDeniedHelp(true);
      } else if (msg.includes('InPrivate') || msg.includes('Incognito') || msg.includes('privado')) {
        toast.error(msg, { duration: 6000 });
      } else if (msg.includes('No device selected') || msg.includes('cancelled')) {
        // Usuário cancelou a seleção - não mostrar erro
      } else {
        toast.error(msg, { duration: 4000 });
      }
    }
  };

  // Conectar via Web Serial API (alternativa ao WebUSB)
  const handleConnectSerial = async () => {
    setUsbPrinter(prev => ({ ...prev, connecting: true }));
    try {
      const result = await thermalPrinter.connectSerial(9600);
      if (result.cancelled) {
        setUsbPrinter(prev => ({ ...prev, connecting: false }));
        return;
      }
      if (result.success) {
        setUsbPrinter({
          isSupported: true,
          isConnected: true,
          deviceName: result.name,
          connecting: false
        });
        toast.success(`Impressora conectada via Serial: ${result.name}`);
        setShowAccessDeniedHelp(false);
        localStorage.setItem('usbPrinterConnected', 'true');
        localStorage.setItem('usbPrinterName', result.name);
        localStorage.setItem('printerConnectionType', 'serial');
      }
    } catch (err) {
      setUsbPrinter(prev => ({ ...prev, connecting: false }));
      toast.error(err.message || 'Erro ao conectar via Serial');
    }
  };

  const handleDisconnectUsb = async () => {
    try {
      await thermalPrinter.disconnect();
      setUsbPrinter({
        isSupported: true,
        isConnected: false,
        deviceName: null,
        connecting: false
      });
      localStorage.removeItem('usbPrinterConnected');
      localStorage.removeItem('usbPrinterName');
      toast.success('Impressora desconectada');
    } catch (err) {
      toast.error('Erro ao desconectar impressora');
    }
  };

  const handleTestUsbPrinter = async () => {
    try {
      await thermalPrinter.printTest();
      toast.success('Teste de impressão enviado!');
    } catch (err) {
      toast.error(err.message || 'Erro no teste de impressão');
    }
  };

  const handleChangeThermalCopies = (value) => {
    const num = parseInt(value, 10) || 2;
    const saved = thermalPrinter.setCopies(num);
    setThermalCopies(saved);
    toast.success('Cópias térmicas alteradas para ' + saved);
  };

  const handleTestPrinter = async (printerId) => {
    try {
      const res = await apiClient.post(`/printers/${storeId}/${printerId}/test`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Erro ao testar impressora');
    }
  };

  const handleSaveDeliveryConfig = async () => {
    try {
      await apiClient.post(`/delivery-config/${storeId}`, deliveryConfig);
      toast.success('Configurações salvas!');
    } catch (err) {
      toast.error('Erro ao salvar configurações');
    }
  };

  const printerTypes = [
    { value: 'KITCHEN', label: 'Cozinha', icon: ChefHat },
    { value: 'RESTAURANT', label: 'Restaurante', icon: Store },
    { value: 'DELIVERY', label: 'Entrega/Motoboy', icon: Truck },
    { value: 'FISCAL', label: 'Fiscal/NFC-e', icon: FileText }
  ];

  const getPrinterTypeLabel = (type) => {
    const found = printerTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
          <Settings className="w-6 h-6 text-red-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurações de Impressão</h1>
          <p className="text-gray-500">Configure impressoras e automação de delivery</p>
        </div>
      </div>

      {/* USB Printer Section - M244A */}
      <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="usb-printer-section">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Usb className="w-5 h-5 text-red-500" />
          Impressora USB Local (M244A / Térmica)
        </h2>
        
        {!usbPrinter.isSupported ? (
          <div className="flex items-center gap-3 p-4 bg-amber-50 text-amber-700 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            <div>
              <p className="font-medium">WebUSB não suportado</p>
              <p className="text-sm">Use o Chrome ou Edge para conectar impressoras USB diretamente.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  usbPrinter.isConnected ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-400'
                }`}>
                  <Printer className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {usbPrinter.isConnected ? (usbPrinter.deviceName || 'Impressora USB') + (thermalPrinter.connectionType === 'serial' ? ' (Serial)' : ' (USB)') : 'Nenhuma impressora conectada'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {usbPrinter.isConnected ? 'Conectada via USB' : 'Clique para conectar uma impressora térmica USB'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {usbPrinter.isConnected ? (
                  <>
                    <button
                      onClick={handleTestUsbPrinter}
                      className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                      data-testid="btn-test-usb-printer"
                    >
                      Testar
                    </button>
                    <button
                      onClick={handleDisconnectUsb}
                      className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                      data-testid="btn-disconnect-usb"
                    >
                      Desconectar
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleConnectUsb}
                      disabled={usbPrinter.connecting}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                      data-testid="btn-connect-usb"
                    >
                      {usbPrinter.connecting ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Usb className="w-4 h-4" />
                      )}
                      {usbPrinter.connecting ? 'Conectando...' : 'Conectar Impressora USB'}
                    </button>
                    {thermalPrinter.isSerialSupported() && (
                      <button
                        onClick={handleConnectSerial}
                        disabled={usbPrinter.connecting}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold"
                        data-testid="btn-connect-serial"
                      >
                        {usbPrinter.connecting ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Wifi className="w-4 h-4" />
                        )}
                        {usbPrinter.connecting ? 'Conectando...' : 'Conectar via Serial (Recomendado)'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Como funciona:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Conecte sua impressora térmica USB (M244A ou compatível ESC/POS)</li>
                <li>• Clique em "Conectar Impressora USB" e selecione o dispositivo</li>
                <li>• A impressora será usada para imprimir pedidos automaticamente</li>
                <li>• Compatível com impressoras de 58mm e 80mm</li>
                <li>• <strong>NAO use modo InPrivate/Incognito</strong></li>
              </ul>
            </div>

            {/* Guia de Access Denied */}
            {showAccessDeniedHelp && (
              <div className="mt-3 bg-red-50 border border-red-300 rounded-lg p-4" data-testid="access-denied-help">
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                  <h4 className="text-sm font-bold text-red-800">Erro: Acesso Negado ao USB</h4>
                </div>
                <div className="text-sm text-red-700 space-y-2 ml-6">
                  <p className="font-medium">Siga estes passos para resolver:</p>
                  <ol className="list-decimal list-inside space-y-1.5">
                    <li><strong className="text-blue-700">Use o botao azul "Conectar via Serial"</strong> — Funciona sem conflito de driver no Windows</li>
                    <li><strong>Feche o modo InPrivate</strong> — Abra o navegador em modo normal</li>
                    <li><strong>Use o Google Chrome</strong> — O Chrome tem melhor suporte</li>
                    <li><strong>Pare o Spooler de Impressao</strong> — No Windows:
                      <ul className="ml-5 mt-1 text-xs space-y-0.5">
                        <li>• Pressione <code className="bg-red-100 px-1 rounded">Win + R</code>, digite <code className="bg-red-100 px-1 rounded">services.msc</code></li>
                        <li>• Encontre <strong>Print Spooler</strong>, clique direito e <strong>Parar</strong></li>
                        <li>• Tente conectar novamente</li>
                      </ul>
                    </li>
                    <li><strong>Desconecte e reconecte</strong> o cabo USB da impressora</li>
                    <li><strong>Feche outros programas</strong> que possam usar a impressora</li>
                  </ol>
                  <button
                    onClick={() => setShowAccessDeniedHelp(false)}
                    className="mt-2 text-xs text-red-500 underline hover:text-red-700"
                  >
                    Fechar esta ajuda
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid="thermal-copies-config">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Cópias por pedido (USB)</h4>
                <p className="text-xs text-gray-500">Quantas vias imprimir por pedido na térmica USB</p>
              </div>
              <select
                value={thermalCopies}
                onChange={(e) => handleChangeThermalCopies(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm w-20"
                data-testid="select-thermal-copies"
              >
                <option value={1}>1 via</option>
                <option value={2}>2 vias</option>
                <option value={3}>3 vias</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Delivery Automation Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="delivery-automation-section">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Truck className="w-5 h-5 text-red-500" />
          Automação de Delivery
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Auto-accept settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">Auto-aceitar Pedidos</h3>
            
            <div className="space-y-3">
              {[
                { key: 'auto_accept_ifood', label: 'iFood', color: 'red' },
                { key: 'auto_accept_99', label: '99 Food', color: 'yellow' },
                { key: 'auto_accept_keeta', label: 'Keeta', color: 'green' },
                { key: 'auto_accept_app', label: 'App Próprio', color: 'blue' },
              ].map(item => (
                <label key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <span className="text-sm text-gray-700">{item.label}</span>
                  <button
                    onClick={() => setDeliveryConfig(prev => ({
                      ...prev,
                      [item.key]: !prev?.[item.key]
                    }))}
                    className={`p-1 rounded-full transition-colors ${
                      deliveryConfig?.[item.key] ? 'text-green-500' : 'text-gray-400'
                    }`}
                    data-testid={`toggle-${item.key}`}
                  >
                    {deliveryConfig?.[item.key] ? (
                      <ToggleRight className="w-8 h-8" />
                    ) : (
                      <ToggleLeft className="w-8 h-8" />
                    )}
                  </button>
                </label>
              ))}
            </div>
          </div>

          {/* Print settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">Configurações de Impressão</h3>
            
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <span className="text-sm text-gray-700">Impressão Automática</span>
              <button
                onClick={() => setDeliveryConfig(prev => ({
                  ...prev,
                  auto_print_enabled: !prev?.auto_print_enabled
                }))}
                className={`p-1 rounded-full transition-colors ${
                  deliveryConfig?.auto_print_enabled ? 'text-green-500' : 'text-gray-400'
                }`}
                data-testid="toggle-auto-print"
              >
                {deliveryConfig?.auto_print_enabled ? (
                  <ToggleRight className="w-8 h-8" />
                ) : (
                  <ToggleLeft className="w-8 h-8" />
                )}
              </button>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Vias Restaurante</label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  value={deliveryConfig?.print_restaurant_copies || 1}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    print_restaurant_copies: parseInt(e.target.value) || 1
                  }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="input-restaurant-copies"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Vias Motoboy</label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  value={deliveryConfig?.print_delivery_copies || 1}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    print_delivery_copies: parseInt(e.target.value) || 1
                  }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="input-delivery-copies"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Impressora Restaurante</label>
                <select
                  value={deliveryConfig?.restaurant_printer_id || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    restaurant_printer_id: e.target.value || null
                  }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="select-restaurant-printer"
                >
                  <option value="">Selecione...</option>
                  {printers.filter(p => p.printer_type === 'RESTAURANT' || p.printer_type === 'KITCHEN').map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Impressora Motoboy</label>
                <select
                  value={deliveryConfig?.delivery_printer_id || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    delivery_printer_id: e.target.value || null
                  }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="select-delivery-printer"
                >
                  <option value="">Selecione...</option>
                  {printers.filter(p => p.printer_type === 'DELIVERY' || p.printer_type === 'RESTAURANT').map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <span className="text-sm text-gray-700">Cadastrar Cliente Automaticamente</span>
              <button
                onClick={() => setDeliveryConfig(prev => ({
                  ...prev,
                  auto_register_customer: !prev?.auto_register_customer
                }))}
                className={`p-1 rounded-full transition-colors ${
                  deliveryConfig?.auto_register_customer ? 'text-green-500' : 'text-gray-400'
                }`}
                data-testid="toggle-auto-register"
              >
                {deliveryConfig?.auto_register_customer ? (
                  <ToggleRight className="w-8 h-8" />
                ) : (
                  <ToggleLeft className="w-8 h-8" />
                )}
              </button>
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveDeliveryConfig}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            data-testid="btn-save-delivery-config"
          >
            <Save className="w-4 h-4" />
            Salvar Configurações
          </button>
        </div>
      </div>

      {/* Printers Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="printers-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Printer className="w-5 h-5 text-red-500" />
            Impressoras Cadastradas
          </h2>
          <button
            onClick={() => setShowAddPrinter(true)}
            className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
            data-testid="btn-add-printer"
          >
            <Plus className="w-4 h-4" />
            Nova Impressora
          </button>
        </div>

        {/* Add Printer Form */}
        {showAddPrinter && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Nova Impressora</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Nome</label>
                <input
                  type="text"
                  value={newPrinter.name}
                  onChange={(e) => setNewPrinter(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Impressora Cozinha"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="input-new-printer-name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tipo</label>
                <select
                  value={newPrinter.printer_type}
                  onChange={(e) => setNewPrinter(prev => ({ ...prev, printer_type: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="select-new-printer-type"
                >
                  {printerTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">IP</label>
                <input
                  type="text"
                  value={newPrinter.ip_address}
                  onChange={(e) => setNewPrinter(prev => ({ ...prev, ip_address: e.target.value }))}
                  placeholder="192.168.1.100"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="input-new-printer-ip"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Porta</label>
                <input
                  type="number"
                  value={newPrinter.port}
                  onChange={(e) => setNewPrinter(prev => ({ ...prev, port: parseInt(e.target.value) || 9100 }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="input-new-printer-port"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Largura Papel (mm)</label>
                <select
                  value={newPrinter.paper_width}
                  onChange={(e) => setNewPrinter(prev => ({ ...prev, paper_width: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value={58}>58mm</option>
                  <option value={80}>80mm</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cópias</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={newPrinter.print_copies}
                  onChange={(e) => setNewPrinter(prev => ({ ...prev, print_copies: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleAddPrinter}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                data-testid="btn-confirm-add-printer"
              >
                <Check className="w-4 h-4" />
                Adicionar
              </button>
              <button
                onClick={() => setShowAddPrinter(false)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Printers List */}
        {printers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Printer className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Nenhuma impressora cadastrada</p>
            <p className="text-sm">Clique em "Nova Impressora" para adicionar</p>
          </div>
        ) : (
          <div className="space-y-3">
            {printers.map(printer => {
              const TypeIcon = printerTypes.find(t => t.value === printer.printer_type)?.icon || Printer;
              
              return (
                <div 
                  key={printer.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    printer.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'
                  }`}
                  data-testid={`printer-${printer.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      printer.is_active ? 'bg-red-100 text-red-600' : 'bg-gray-200 text-gray-400'
                    }`}>
                      <TypeIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{printer.name}</h3>
                      <p className="text-sm text-gray-500">
                        {getPrinterTypeLabel(printer.printer_type)} • {printer.ip_address || 'Sem IP'} : {printer.port} • {printer.paper_width}mm
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleTestPrinter(printer.id)}
                      className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                      data-testid={`btn-test-printer-${printer.id}`}
                    >
                      Testar
                    </button>
                    <button
                      onClick={() => handleDeletePrinter(printer.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      data-testid={`btn-delete-printer-${printer.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </MainLayout>
  );
};

export default PrinterSettingsPage;
